import { cva, type VariantProps } from 'class-variance-authority'
import { SearchIcon } from 'lucide-react'
import { type ComponentProps, useRef } from 'react'
import { cn } from '@/src/utils/styles'
import { Button } from './button'

const searchFieldVariants = cva(
  'flex items-center gap-2 bg-surface1 text-secondary hover:bg-surface2 hover:text-primary transition-all focus-visible:outline focus-visible:outline-2 focus-visible:outline-focus focus-visible:outline-offset-2',
  {
    variants: {
      dimension: {
        small: 'h-8 rounded-full py-1 px-3',
        medium: 'h-12 rounded-full py-3 px-4 gap-3',
        large: 'h-14 rounded-full py-4 pl-5 pr-1 gap-3',
      },
    },
    defaultVariants: {
      dimension: 'small',
    },
  },
)

interface SearchFieldProps
  extends ComponentProps<'label'>,
    VariantProps<typeof searchFieldVariants> {
  inputProps?: Omit<ComponentProps<'input'>, 'className'>
  inputClassName?: string
  resetState?: () => void
}

const SearchField = ({
  className,
  dimension,
  inputProps,
  inputClassName,
  resetState,
  ref,
  ...props
}: SearchFieldProps) => {
  const inputRef = useRef<HTMLInputElement>(null)

  return (
    <div className='relative w-full'>
      <label
        ref={ref}
        className={cn(
          'group relative peer cursor-text',
          searchFieldVariants({ dimension, className }),
        )}
        onClick={(e) => {
          if (inputProps?.ref && 'current' in inputProps.ref) {
            inputProps.ref.current?.focus()
          } else {
            inputRef.current?.focus()
          }
          props.onClick?.(e)
        }}
        {...props}
      >
        {dimension === 'large' ? (
          <SearchIcon className='shrink-0' size={24} />
        ) : (
          <SearchIcon className='shrink-0' size={16} />
        )}
        <input
          ref={inputRef}
          type='text'
          className={cn(
            'bg-transparent font-[420] placeholder:text-input-placeholder hover:placeholder:text-input-placeholder-hover focus:outline-none focus:ring-0 focus-visible:outline-none focus-visible:text-primary focus-visible:placeholder:text-input-placeholder-focus focus-visible:ring-0 transition-colors',
            inputClassName,
          )}
          {...inputProps}
        />
      </label>
      <Button
        type='button'
        className={cn(
          'absolute z-10 right-5 inset-y-5 size-2 rounded-full opacity-0 pointer-events-none transition-all',
          {
            'opacity-100 pointer-events-auto before:!bg-surface1-100 before:peer-hover:!bg-surface2-100':
              (inputProps?.value?.toString().trim().length ?? 0) > 0,
          },
        )}
        size='sm'
        onClick={resetState}
        squared
      >
        <svg
          className='min-w-4'
          width='16'
          height='16'
          viewBox='0 0 16 16'
          fill='none'
          xmlns='http://www.w3.org/2000/svg'
        >
          <path
            fillRule='evenodd'
            clipRule='evenodd'
            d='M8.00049 16C12.4188 16 16.0005 12.4183 16.0005 8C16.0005 3.58172 12.4188 0 8.00049 0C3.58221 0 0.000488281 3.58172 0.000488281 8C0.000488281 12.4183 3.58221 16 8.00049 16ZM11.5304 4.46958C11.8234 4.76242 11.8234 5.23729 11.5306 5.53024L9.06047 8.00125L11.5306 10.4723C11.8234 10.7652 11.8234 11.2401 11.5304 11.5329C11.2375 11.8258 10.7626 11.8257 10.4698 11.5327L8 9.0621L5.53025 11.5327C5.23741 11.8257 4.76253 11.8258 4.46959 11.5329C4.17664 11.2401 4.17656 10.7652 4.4694 10.4723L6.93953 8.00125L4.4694 5.53024C4.17656 5.23729 4.17664 4.76242 4.46959 4.46958C4.76253 4.17673 5.23741 4.17682 5.53025 4.46976L8 6.9404L10.4698 4.46976C10.7626 4.17682 11.2375 4.17673 11.5304 4.46958Z'
            fill='white'
            fillOpacity='0.44'
          />
        </svg>
      </Button>
    </div>
  )
}

SearchField.displayName = 'SearchField'

export { SearchField }
